import * as React from "react";
import classNames from "classnames";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps } from "react-i18next";
import { Dispatch } from "redux";
import { connect } from "react-redux";

import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import Avatar from "@material-ui/core/Avatar";
import PhoneIcon from "@material-ui/icons/Phone";
import CloseIcon from "@material-ui/icons/Close";
import AlternateEmailIcon from "@material-ui/icons/AlternateEmail";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import InfoIcon from "@material-ui/icons/Info";
import RestaurantIcon from "@material-ui/icons/RestaurantMenu";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import OpenWithIcon from "@material-ui/icons/OpenWith";
import { format, formatDate } from "../../util/date-utility";
import { Reservation } from "../../api/reservations";
import { Table } from "../../api/tables";
import { StoreState } from "../../reducers/root-reducer";
import { openEditReservationDialog } from "../../actions/store/app-actions";
import { setMovingReservation } from "../../actions/store/planner-list-actions";
import { Area } from '../../api/areas';
import { Meal } from "../../api/meals";
import { getReadablePaymentStatus } from "../../util/general-utility";

export interface PlannerListReservationProps extends Reservation {
	readOnly?: Boolean;
}

interface ConnectedPlannerListReservationProps
	extends WithStyles<typeof styles> {
	tables: Table[];
	areas: Area[];
	overviewInfoBarVisible: Boolean;
	movingReservationId: string;
	availableMeals: Meal[];
	setMovingReservation: (id: string) => void;
	openEditReservationDialog: (reservation: Reservation) => void;
}

type Props = PlannerListReservationProps & ConnectedPlannerListReservationProps;

export class PlannerListReservation extends React.Component<Props, {}> {
	private handleMoveClick = () => {
		const {
			id,
			movingReservationId,
			setMovingReservation,
			readOnly
		} = this.props;

		if (readOnly) return;

		if (movingReservationId !== id) {
			setMovingReservation(id);
		} else {
			setMovingReservation(null);
		}
	};

	private handleEditClick = () => {
		const { openEditReservationDialog, readOnly } = this.props;

		if (readOnly) return;

		openEditReservationDialog(this.props);
	};

	private renderSubheader = () => {
		const { tableId, time, date, inquiredArea, areas, meals, classes } = this.props;

		let prefix = '';
		if (meals && Object.keys(meals).length > 0) {
			prefix = '<i class="material-icons ' + classes.icon + '">restaurant_menu</i>&nbsp;';
		}

		if (inquiredArea && !tableId) {
			const area = areas.find(a => a.id === inquiredArea);

			if (area) {
				return <span dangerouslySetInnerHTML={{ __html: (prefix + 'Bereich: ' + area.name) }} />;
			}
		}

		let subheader = time ? format(time, "HH:mm") + ' Uhr' : formatDate(date);

		return <span dangerouslySetInnerHTML={{ __html: prefix + subheader }} />;
	}

	private renderAction = () => {
		const { classes, id, movingReservationId, readOnly } = this.props;

		if (readOnly) return null;

		return (
			<IconButton
				className={classes.moveButton}
				onClick={this.handleMoveClick}
			>
				{movingReservationId === id ? <CloseIcon /> : <OpenWithIcon />}
			</IconButton>
		);
	};

	public render() {
		const {
			classes,
			id,
			overviewInfoBarVisible,
			date,
			peopleCount,
			primaryName,
			time,
			phoneNumber,
			email,
			notes,
			inquiryId,
			payment,
			movingReservationId,
			checkedIn,
			meals,
			availableMeals
		} = this.props;

		return (
			<Card
				className={classNames(classes.root, {
					[classes.rootActive]: movingReservationId === id,
					[classes.rootStatusBookedOnline]: Boolean(inquiryId),
					[classes.rootStatusHasPayment]: payment && payment != null,
					[classes.rootStatusPayed]: payment && payment.state === "completed",
					[classes.rootStatusPaymentExpired]: payment && payment.state === "expired",
					[classes.rootCheckedIn]: checkedIn,
				})}
			>
				<CardHeader
					avatar={
						<Avatar onClick={this.handleEditClick}>
							{peopleCount}
						</Avatar>
					}
					title={primaryName}
					subheader={this.renderSubheader()}
					classes={{
						action: classes.cardAction
					}}
					titleTypographyProps={{
						onClick: this.handleEditClick
					}}
					subheaderTypographyProps={{
						onClick: this.handleEditClick
					}}
					action={this.renderAction()}
				/>
				<CardContent
					className={classNames(classes.content, {
						[classes.contentHidden]: !overviewInfoBarVisible
					})}
				>
					<div
						className={classNames(
							classes.dataGroup,
							classes.peopleCount
						)}
					>
						<div className={classes.dataIconContainer}>
							<PeopleIcon className={classes.dataIcon} />
						</div>
						<Typography>{peopleCount} Personen</Typography>
					</div>
					{time && (
						<div
							className={classNames(
								classes.dataGroup,
								classes.time
							)}
						>
							<div className={classes.dataIconContainer}>
								<AccessTimeIcon className={classes.dataIcon} />
							</div>
							<Typography>{format(time, "HH:mm")} Uhr</Typography>
						</div>
					)}
					{phoneNumber && (
						<div
							className={classNames(
								classes.dataGroup,
								classes.phoneNumber
							)}
						>
							<div className={classes.dataIconContainer}>
								<PhoneIcon className={classes.dataIcon} />
							</div>
							<Typography>{phoneNumber}</Typography>
						</div>
					)}
					{email && (
						<div
							className={classNames(
								classes.dataGroup,
								classes.email
							)}
						>
							<div className={classes.dataIconContainer}>
								<AlternateEmailIcon
									className={classes.dataIcon}
								/>
							</div>
							<Typography>{email}</Typography>
						</div>
					)}
					{payment && (
						<div className={classes.dataGroup}>
							<div className={classes.dataIconContainer}>
								<AttachMoneyIcon className={classes.dataIcon} />
							</div>
							<Typography>
								<span className={classNames(classes.paymentState, {
									[classes.paymentNotCompleted]: payment.state !== "completed",
								})}>
									{getReadablePaymentStatus(payment)}
								</span>
							</Typography>
						</div>
					)}
					{meals && Object.keys(meals).length > 0 && (
						<div className={classes.dataGroup}>
							<div className={classes.dataIconContainer}>
								<RestaurantIcon className={classes.dataIcon} />
							</div>
							<Typography>
								{Object.keys(meals).map(id => (
									<div key={id}>{meals[id]}x {availableMeals.find(m => m.id === id).name}</div>
								))}
							</Typography>
						</div>
					)}
					{notes && (
						<div
							className={classNames(
								classes.dataGroup,
								classes.notes
							)}
						>
							<div className={classes.dataIconContainer}>
								<InfoIcon className={classes.dataIcon} />
							</div>
							<Typography>{notes}</Typography>
						</div>
					)}
				</CardContent>
			</Card>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables,
	areas: state.areas,
	overviewInfoBarVisible: state.overviewInfoBarVisible,
	movingReservationId: state.plannerListMovingReservation,
	availableMeals: state.settings.meals
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setMovingReservation: (id: string) => dispatch(setMovingReservation(id)),
	openEditReservationDialog: (reservation: Reservation) =>
		dispatch(openEditReservationDialog(reservation))
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			margin: 10,
			width: "calc(33.33% - 20px)",
			display: "flex",
			flexDirection: "column",
			borderLeft: "6px solid " + theme.status.reservation.manual,

			"@media (max-width: 1023px)": {
				width: "calc(50% - 20px)"
			},
			"@media (max-width: 767px)": {
				width: "calc(100% - 20px)"
			}
		},
		rootActive: {
			opacity: 0.5
		},
		rootStatusBookedOnline: {
			borderColor: theme.status.reservation.bookedOnline
		},
		rootStatusHasPayment: {
			borderColor: theme.status.reservation.awaitingPayment
		},
		rootStatusPaymentExpired: {
			borderColor: theme.status.reservation.expiredPayment
		},
		rootStatusPayed: {
			borderColor: theme.status.reservation.payed
		},
		rootCheckedIn: {
			opacity: 0.3
		},
		content: {
			background: "#FAFAFA",
			flexGrow: 1
		},
		contentHidden: {
			display: "none"
		},
		dataGroup: {
			display: "flex",

			"* + &": {
				marginTop: 5
			}
		},
		dataIconContainer: {
			marginRight: 10
		},
		dataIcon: {
			color: "#797979"
		},
		cardAction: {
			alignSelf: "center"
		},
		peopleCount: {},
		time: {},
		phoneNumber: {},
		email: {},
		notes: {},
		moveButton: {},
		icon: {
			fontSize: "initial",
			color: theme.palette.primary.main,
			transform: "translateY(2px)"
		},
		paymentState: { color: "green" },
		paymentNotCompleted: { color: "red" }
	});

export const ConnectedPlannerListReservation = withStyles(styles)(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(PlannerListReservation)
);
